import { CatalogResourceDc, CatalogResourceType } from "@evergis/api";
import { Input, Textarea } from "@evergis/ui";
import React, { ChangeEvent, Dispatch, FC, SetStateAction, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FileImagePreview } from "../../../../modules/FeatureCard/components/Attributes/components/Attachments/FileImagePreview";
import { getFileTypeIcon } from "../../../../modules/FeatureCard/components/Attributes/components/Attachments/getFileTypeIcon";
import { getResourceType } from "../../../../modules/FeatureCard/components/Attributes/components/Attachments/getResourceType";
import {
  CATALOG_RESOURCE_TYPES,
  FileType,
  IMAGE_FILE_TYPES,
} from "../../../../modules/FeatureCard/components/Attributes/components/Attachments/types";
import { PathField } from "../../DialogComponents/Field/PathField";
import { SystemNameField } from "../../DialogComponents/Field/SystemNameField";
import folderPreview from "../assets/folder_preview.svg";
import {
  PreviewIconFile,
  PreviewIconFolderContainer,
  PreviewIconResource,
  PreviewIconResourceContainer,
} from "../styled";
import { EditResourceMeta, EditResourcePreview, EditResourceWrapper } from "./styled";
import { TagsInput } from "./TagsInput";

export interface EditResourceProps {
  item: CatalogResourceDc;
  name: string;
  setName: Dispatch<SetStateAction<string>>;
  description: string;
  setDescription: Dispatch<SetStateAction<string>>;
  tags: string[];
  setTags: Dispatch<SetStateAction<string[]>>;
  withSystemName?: boolean;
  setSystemName?: Dispatch<SetStateAction<string>>;
  systemName?: string;
  withPath?: boolean;
  setPath?: Dispatch<SetStateAction<CatalogResourceDc[]>>;
  path?: CatalogResourceDc[];
}

export const extractResourceNameFormatted = (resource = ""): string => {
  const nameSpace = resource.split(".")[0];

  return resource.replace(`${nameSpace}.`, "");
};

export const extractNameSpaceFormatted = (resource = ""): string => {
  return resource.split(".")[0];
};

export const EditResource: FC<EditResourceProps> = ({
  item = {},
  name,
  setName,
  description,
  setDescription,
  tags,
  setTags,
  withSystemName,
  setSystemName,
  systemName,
  withPath,
  setPath,
  path,
}) => {
  const { t } = useTranslation("catalog");

  const { resourceId, isDirectory, type } = item;

  const fileType = getResourceType(item);
  const isImg = IMAGE_FILE_TYPES.includes(fileType);
  const isCatalogResource = CATALOG_RESOURCE_TYPES.includes(fileType);

  const updateSystemName = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSystemName(`${extractNameSpaceFormatted(systemName)}.${e.target.value}`);
    },
    [setSystemName, systemName],
  );

  return (
    <EditResourceWrapper>
      <EditResourcePreview>
        {!isImg && (isCatalogResource || fileType === FileType.PostgresSchema) && (
          <PreviewIconResourceContainer
            style={{ width: "100%", height: "100%", borderRadius: "10px" }}
            fileType={fileType}
            type={type}
          >
            <PreviewIconResource
              style={{ width: "75%", height: "100%", borderRadius: "10px" }}
              fileType={fileType}
              src={
                isDirectory && fileType !== FileType.PostgresSchema ? folderPreview : getFileTypeIcon(fileType, type)
              }
            />
          </PreviewIconResourceContainer>
        )}
        {!isImg && !(isCatalogResource || fileType === FileType.PostgresSchema) && (
          <PreviewIconFolderContainer isFolder={isDirectory}>
            <PreviewIconFile isDirectory={isDirectory && type !== CatalogResourceType.DataSource} fileType={fileType} />
          </PreviewIconFolderContainer>
        )}
        {isImg && <FileImagePreview link={resourceId} isExternal={false} size={"100%"} borderRadius={"10px"} />}
      </EditResourcePreview>
      <EditResourceMeta>
        <Input width="100%" required label={t("tableName")} value={name} onChange={e => setName(e.target.value)} />
        {withPath && <PathField resource={item} width={"100%"} value={path} onChange={setPath} />}
        {!withSystemName && !!systemName && (
          <SystemNameField
            type="text"
            label={t("systemName", { ns: "common" })}
            value={extractResourceNameFormatted(systemName)}
            valuePrefix={extractNameSpaceFormatted(systemName)}
            onChange={updateSystemName}
            width="100%"
            disabled={!withSystemName}
            resourceType={type}
            noCheck={!withSystemName}
          />
        )}
        {withSystemName && (
          <SystemNameField
            type="text"
            label={t("systemName", { ns: "common" })}
            value={extractResourceNameFormatted(systemName)}
            valuePrefix={extractNameSpaceFormatted(systemName)}
            onChange={updateSystemName}
            width="100%"
            resourceType={type}
          />
        )}
        <Textarea
          width="100%"
          label={t("description")}
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
        <TagsInput
          width="100%"
          label={t("tags")}
          tags={tags}
          onTagAdd={tag => setTags(current => [...current, tag])}
          onTagDelete={tag => setTags(current => current.filter(val => val !== tag))}
        />
      </EditResourceMeta>
    </EditResourceWrapper>
  );
};
